import DeleteConfirmationDialog from "@/components/layouts/DeleteConfirmationDialog";
import { useUser } from "@/contexts/UserContext";
import type { Employee } from "@/utils/types";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faEdit, faEye, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import BranchRequiredWrapper from "../../../banners/BranchRequiredWrapper";
import PageHeader from "../../../layouts/PageHeader";
import PageLoader from "../../../loader/PageLoader";
import { Button } from "../../../ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";

const Employee = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);

  const openDeleteConfirmation = (id: number) => {
    setItemToDelete(id);
    setDeleteConfirmationOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setItemToDelete(null);
    setDeleteConfirmationOpen(false);
  };

  const fetchEmployees = async () => {
    if (loading || !axiosInstance) return;
    try {
      const res = await axiosInstance.get(
        `/api/v1/employee?branch=${user?.selectedBranch?.id}`,
      );
      if (res.data.results) {
        setEmployees(res.data.results);
      }
    } catch (error) {
      toast.error("Failed to fetch employees");
      console.error(error);
    }
  };

  const confirmDelete = async () => {
    if (loading || !axiosInstance || !itemToDelete) return;
    try {
      await axiosInstance.delete(`/api/v1/employee/${itemToDelete}`);
      fetchEmployees();
      toast.success("Employee Removed successfully.");
    } catch (error) {
      console.error("Error removing employee: ", error);
      toast.error("Failed to remove employee");
    } finally {
      closeDeleteConfirmation();
    }
  };

  useEffect(() => {
    if (user?.selectedBranch?.id) {
      fetchEmployees();
    }
  }, [axiosInstance, loading, user]);

  return (
    <PageLoader isFetching={isFetching}>
      <BranchRequiredWrapper>
        <PageHeader
          title={t("back.management.menu.employee.title")}
          exportLabel={t("back.management.menu.employee.export")}
          addLabel={t("back.management.menu.employee.addemployee")}
          exportEnabled={false}
          onExport={() => {}}
          onAdd={() => navigate("/employee/add")}
        />
        <DeleteConfirmationDialog
          isOpen={deleteConfirmationOpen}
          onClose={closeDeleteConfirmation}
          onConfirm={confirmDelete}
          title="Remove Employee"
          message="Are you sure you want to remove this employee?"
        />
        <div className="flex flex-col space-y-6 rounded-lg bg-white p-4 shadow">
          <div className="flex flex-col space-y-3">
            <h1 className="font-bold lg:text-lg">Owner</h1>
            <p>{user?.username}</p>
          </div>
          <div>
            <h1 className="font-bold lg:text-lg">Server</h1>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("back.management.menu.employee.name")}</TableHead>
                <TableHead>
                  {t("back.management.menu.employee.jobtitle")}
                </TableHead>
                <TableHead className="text-center">
                  {t("back.management.menu.employee.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((employee) => (
                <TableRow key={employee.id}>
                  <TableCell>
                    {employee.first_name} {employee.last_name}
                  </TableCell>
                  <TableCell>
                    {(employee.job_title ?? "").charAt(0).toUpperCase() +
                      (employee.job_title ?? "").slice(1).toLowerCase()}
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      onClick={() => navigate(`/employee/${employee.id}`)}
                    >
                      <FontAwesomeIcon icon={faEye} className="text-red-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => navigate(`/employee/edit/${employee.id}`)}
                    >
                      <FontAwesomeIcon icon={faEdit} className="text-red-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => openDeleteConfirmation(employee.id || 0)}
                    >
                      <FontAwesomeIcon
                        icon={faTrash}
                        className="text-gray-500"
                      />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </BranchRequiredWrapper>
    </PageLoader>
  );
};

export default Employee;
